import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  CheckIcon,
  XIcon,
  PencilSimpleIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface InlineEditableInvoiceNumberProps {
  value: string | null;
  onUpdate: (value: string | null) => Promise<void>;
  isLoading?: boolean;
  className?: string;
}

export function InlineEditableInvoiceNumber({
  value,
  onUpdate,
  isLoading = false,
  className,
}: InlineEditableInvoiceNumberProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset edit value when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || '');
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    try {
      await onUpdate(editValue.trim() || null);
      setIsEditing(false);
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value || '');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          className="h-7 text-sm font-mono"
          onClick={(e) => e.stopPropagation()}
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            void handleSave();
          }}
          disabled={isLoading}
          className="h-7 w-7 p-0"
        >
          {isLoading ? (
            <CircleNotchIcon className="h-3 w-3 animate-spin" />
          ) : (
            <CheckIcon className="h-3 w-3 text-green-600" />
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleCancel();
          }}
          disabled={isLoading}
          className="h-7 w-7 p-0"
        >
          <XIcon className="h-3 w-3 text-red-600" />
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn('flex items-center gap-1 group', className)}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="text-sm font-mono">{value || '-'}</span>
      <Button
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        className="h-7 w-7 p-0"
      >
        <PencilSimpleIcon className="h-3 w-3" />
      </Button>
    </div>
  );
}
